import React, { useEffect, useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { Endpoints } from '../../Global/Endpoints';
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import '../MaternityHighRiskAssessment/maternitystyles.css';
import ErrorModal from "../Security/ErrorModal";

const HighRiskMaternityAssessment = () => {
  const userInfo = useSelector((state) => state.userData);
  const token = localStorage.getItem('AccessToken');
  const { t, i18n } = useTranslation();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [message, setMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const navigate = useNavigate();
  const [extraInfo, setExtraInfo] = useState({});
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const ssoData = useSelector((state) => state.sso.ssoUserData);
  const isHighmarkPortal = ssoData?.Portal === "Highmark";
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        
        const params = {
          MemberID: userInfo.userData.MemberID,
          Contract: userInfo.userData.ContractIdentified || "",
          EmailID: userInfo.userData.EmailID,
          DeviceType: "PatientPortal",
        };
  
        const response = await axios.post(
          Endpoints.GetHighRiskMaternityAssessment,
          { _Params: JSON.stringify(params) },
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
              "Connection": "close",
              "Accept": "*/*",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        
        if (response.data.result === 1) {
  
          const initialData = [
            { key: "partake", question: "Do you partake in any of the following? Check all that apply.", options: ["Smoke cigarettes or use tobacco products", "Drink alcohol", "Use any recreational drugs or substances that are not prescribed to you,including cannabis"] },
            { key: "anyoffollowing", question: "Do you have any of the following? Check all that apply.", options: ["High blood pressure", "Diabetes", "Epilepsy", "Thyroid Disease", "Heart condition", "Any blood-clotting disorder"] },
            { key: "symptoms", question: "If you have asthma, how well controlled are your symptoms?", options: ["Well-controlled", "Not well-controlled", "I do not have asthma"] },
            { key: "twin", question: "Are you carrying a twin pregnancy or more than two babies?", options: ["Yes", "No"] },
            { key: "unusualposition", question: "Has your provider told you your placenta is in an unusual position?", options: ["Yes", "No"] },
            { key: "smallerbaby", question: "Has your provider expressed concerns about your baby (or babies) being smaller than expected—below the 10th percentile?", options: ["Yes", "No"] },
            { key: "largerbaby", question: "Has your provider expressed concerns about your baby (or babies) being larger than expected or having higher amniotic fluid level than expected?", options: ["Yes", "No"] },
            { key: "historyofpreeclampsia", question: "Do you have a family history of preeclampsia (i.e., did your mother have preeclampsia)?", options: ["Yes", "No", "Not sure"] },
            { key: "pregnancylosses", question: "Have you experienced two or more miscarriages (pregnancy losses) previously?", options: ["Yes", "No"] },
            { key: "conceivedivf", question: "Is this pregnancy conceived via In-Vitro Fertilization (IVF)?", options: ["Yes", "No"] },
            { key: "medicalconditions", question: "Do you have any other chronic or past medical conditions that may affect your pregnancy care?", options: ["Yes", "No"] },
            { key: "medication", question: "Do you take any medications for any chronic medical conditions, including mental health conditions?", options: ["Yes", "No"] },
          ];
        
          setQuestions(initialData);

           // **Map API response correctly**
        const optionReverseMapping = {
          "a": "Smoke cigarettes or use tobacco products",
          "b": "Drink alcohol",
          "c": "Use any recreational drugs or substances that are not prescribed to you,including cannabis",
        };

        const savedAnswers = {};
        let hasExistingData = false;
        initialData.forEach((item) => {
          const apiValue = response.data[item.key];

          if (item.key === "partake" && apiValue) {
            // Convert API stored values ('a,b,c') back to readable options
            savedAnswers[item.key] = apiValue
              .split(",")
              .map((char) => optionReverseMapping[char.trim()])
              .filter(Boolean)
              .join(", ");
              hasExistingData = true; // Mark that data exists
          } else if (apiValue !== undefined && apiValue !== "") {
            hasExistingData = true; // Mark data presence if any value exists
            if (apiValue === "Y") {
              savedAnswers[item.key] = "Yes";
            } else if (apiValue === "N") {
              savedAnswers[item.key] = "No";
            } else if (apiValue === "C") {
              savedAnswers[item.key] = "Not sure";
            } else {
              savedAnswers[item.key] = apiValue;
            }
          } else {
            savedAnswers[item.key] = "";
          }
        });
     // **Check if the user selected any answers**
     const userHasSelectedAnswers = Object.values(savedAnswers).some((answer) => answer.trim() !== "");
        setAnswers(savedAnswers);

        // **Ensure extra input fields are mapped correctly**
        setExtraInfo({
          medicalconditionstext: response.data.medicalconditionstext || "",
          medicationtext: response.data.medicationtext || "",
        });
        setIsFirstLoad(!userHasSelectedAnswers); // Update the isFirstLoad state
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false); // <-- Ensure loading is set to false after fetch
    }
  };

  fetchData();
}, []);
  

  // const handleToggle = (key, value) => {
  //   setAnswers((prev) => ({ ...prev, [key]: value }));
  // };
  const handleToggle = (key, value) => {
    setAnswers((prev) => {
      if (key === "partake" || key === "anyoffollowing") {
        // Ensure the value is always an array (convert from string if needed)
        const selectedOptions = prev[key] ? prev[key].split(", ") : [];
  
        let updatedOptions;
        if (selectedOptions.includes(value)) {
          // If already selected, remove it (deselect)
          updatedOptions = selectedOptions.filter((item) => item !== value);
        } else {
          // Otherwise, add the selection
          updatedOptions = [...selectedOptions, value];

        }
  
        return { ...prev, [key]: updatedOptions.join(", ") };
      } else {
        // For other questions (single selection)
        return { ...prev, [key]: value };
      }
    });
  };
  const handleExtraInfoChange = (key, value) => {
    setExtraInfo((prev) => ({
      ...prev,
      [key]: value,
    }));
  };
  
  const handleSubmit = async (event) => {
    event.preventDefault();
     // Check if at least one question has been answered
  const hasSelection = Object.values(answers).some(answer => answer && answer.trim() !== "");

  if (!hasSelection) {
    setModalMessage("Please select at least one checkbox before submitting.");
    setShowModal(true);
    return;
  }
    const params = {
      ...answers,
      MemberID: userInfo?.userData?.MemberID || "",
      Contract: userInfo?.userData?.ContractIdentified || "",
      EmailID: userInfo?.userData?.EmailID || "",
      DeviceType: "PatientPortal",
    };
 // **Convert Yes/No values to Y/N**
 Object.keys(params).forEach((key) => {
  if (params[key] === "Yes") {
    params[key] = "Y";
  } else if (params[key] === "No") {
    params[key] = "N";
  }else if (params[key] === "Not sure") { 
    params[key] = "C";
  }
});
// **Map 'partake' question answers to 'a', 'b', 'c'**
if (answers.partake) {
  const optionMapping = {
    "Smoke cigarettes or use tobacco products": "a",
    "Drink alcohol": "b",
    "Use any recreational drugs or substances that are not prescribed to you,including cannabis": "c",
  };

  const selectedOptions = answers.partake.split(", ").map(option => optionMapping[option]).filter(Boolean);
  params.partake = selectedOptions.join(","); // Convert to "a,b" format
}
// **Ensure extra text fields are saved correctly**
if (answers.medicalconditions === "Yes") {
  params.medicalconditionstext = extraInfo.medicalconditionstext || "";
}
if (answers.medication === "Yes") {
  params.medicationtext = extraInfo.medicationtext || "";
}

    try {
      
      const response = await axios.post(
        Endpoints.SaveHighRiskMaternityAssessment,
        { _Params: JSON.stringify(params) },
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.result === 1) {
        
        setModalMessage("Details Saved Successfully");
        setShowModal(true);
      } else {
        setModalMessage("Failed to save details. Please try again.");
        setShowModal(true);
      }
    } catch (error) {
      setModalMessage("An error occurred. Please try again later.");
      setShowModal(true);
    }
  };
  const handleBackClick = () => {
    navigate(-1); 
  };
  const handleModalClose = () => {
    navigate(-1);
  };
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading"></div>        
        <h2 className="loading-text">{t("loading")}</h2>
      </div>
    );
  }

  return (
    <>
      
      {isHighmarkPortal ? 
        <div className="col-md-12 mt-4 d-flex inner-page-heading-wrapper">
          <button className="win-back-btn" onClick={handleBackClick}>
            <span>
              <i className="bi bi-chevron-left"></i>
            </span>
          </button>       
          <h2 className="page-heading">Let's Complete Your Pregnancy Assessment</h2>
        </div>:
        <div className="col-md-12 mt-4 d-flex inner-page-heading-wrapper">
          <button className="win-back-btn" onClick={handleBackClick}>
            <span>
              <i className="bi bi-chevron-left"></i>
              {t('Back')}
            </span>
          </button>       
          <h2 className="page-heading">Let's Complete Your Pregnancy Assessment</h2>
        </div>
      }
      
      {/* <div className="col-md-12 col-lg-12 mt-md-4 mt-sm-0 inner-page-heading-wrapper">
          <button onClick={handleBackClick} className="win-back-btn">
            <span><i  className="bi bi-chevron-left"></i> {t('back')}</span>
          </button>
          <h2 className="page-heading">Let's Complete Your Pregnancy Assessment</h2>
        </div> */}
      <p>
  {isFirstLoad ? (
    <>
      {t('pregnancySupportMessage')}
      <p>{t('highRiskAssessmentMessage')}</p>
    </>
  ) : (
    isHighmarkPortal ? (
      <p>
        It’s almost time for your next WIN Nurse Care Advocate consult! To ensure your WIN Nurse can provide tailored support and guidance, please take 2-3 minutes to review your Pregnancy Assessment.      </p>
    ) : (
      <p>
        It’s almost time for your next WIN Nurse Care Advocate consult! To ensure your WIN Nurse can provide tailored support and guidance, please take 2-3 minutes to review your Pregnancy Assessment.
      </p>
    )
  )}
      </p>
      {/* <p>
      {t('highRiskAssessmentMessage')}
      </p> */}
      {/* <form onSubmit={handleSubmit}>
      {questions.map((item) => (
          <div key={item.key} className="row mb-3">
            <div className="col-1">
              <div className="switch-control-wrapper ms-auto">
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={answers[item.key] === "Y"}
                    className="switch-input"
                    onChange={(e) =>
                      handleToggle(item.key, e.target.checked ? "Y" : "N")
                    }
                  />
                  <span className="slider"></span>
                </label>                
                <span className="label"> {answers[item.key] === "Y" ? "Yes" : "No"}</span>
              </div>
            </div>
            <div className="col-11">
              <label>{item.question}</label>
            </div> */}
            {/* <div>
              <button
                type="button"
                className={answers[item.key] === "Y" ? "selected yes-button" : "yes-button"}
                onClick={() => handleToggle(item.key, "Y")}
              >
                Yes
              </button>
              <button
                type="button"
                className={answers[item.key] === "N" ? "selected no-button" : "no-button"}
                onClick={() => handleToggle(item.key, "N")}
              >
                No
              </button>
            </div> */}
            
          {/* </div>
        ))}
        <div className="mb-5 mt-5 d-flex">
        <button className="win-btn-primary mx-auto" type="submit">{t('submit')}</button>
          </div>
      </form> */}
       <form onSubmit={handleSubmit}>
        <ol>
  {questions.map((item) => (
    
      <li key={item.key} className="multistep-form-radio-checkbox-label">{item.question}
      {item.options.map((option, index) => (
        <div key={index}>
          <div className="mb-1 form-check multi-step-form-rdb-chekcbox-wrapper">
            <input
              type="checkbox"
              className="form-check-input radio-btn win-form-check-input"
              name={item.key === "partake" || item.key === "anyoffollowing" ? `${item.key}_${index}` : item.key}
              value={option}
              checked={item.key === "partake" || item.key === "anyoffollowing"
                ? answers[item.key]?.split(", ").includes(option)
                : answers[item.key] === option}
              onClick={() => handleToggle(item.key, option)} // Handles selection & deselection
            />
            <label className="multistep-form-radio-checkbox-label">{option}</label>
          </div>
        </div>
      ))}
    {/* Extra input field when selecting "Yes" */}
    {item.key === "medicalconditions" && answers[item.key] === "Yes" && (
      <div className="ms-5">
        <textarea
          className="form-control win-form-control textarea-control mobile-view mt-2"
          placeholder="Please provide more information"
          value={extraInfo.medicalconditionstext}
          onChange={(e) => handleExtraInfoChange("medicalconditionstext", e.target.value)}
        />
      </div>
    )}

    {item.key === "medication" && answers[item.key] === "Yes" && (
      <div className="ms-5">
        <textarea
          className="form-control win-form-control textarea-control mobile-view mt-2"
          placeholder="Please provide more information"
          value={extraInfo.medicationtext}
          onChange={(e) => handleExtraInfoChange("medicationtext", e.target.value)}
        />
      </div>
    )}
  </li>
  ))}
  </ol>
  <button type="submit" className="win-btn-primary model-popup-btn mb-5 ms-4">
    {t("submit")}
  </button>
</form>

      <ErrorModal show={showModal} message={modalMessage} onClose={handleModalClose}  />
    </>
  );
};

export default HighRiskMaternityAssessment;